import { React, useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Edit, Delete } from '@mui/icons-material';
import Progress from '../Progress';
import AddIncomeTaxRulesForm from '../forms/AddIncomeTaxRulesForm';
import Popup from '../Popup';
import Notification from '../Notification';
import ConfirmDialog from '../ConfirmDialog';

const IncomeTaxRulesTable = forwardRef((props, ref) => {
    const [openPopup, setOpenPopup] = useState(false);
    const [fetch_id, setFetchId] = useState(0);
    const [incomeTaxRules, setIncomeTaxRules] = useState([]);
    const [notify, setNotify] = useState({ isOpen: false, message: '', type: '' });
    const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, title: '', subTitle: '' });
    const [loading, setLoading] = useState(true);
    const selectedFinancialYear = "2025-2026"; // Hard coded financial year

    const navigate = useNavigate();

    async function getAllIncomeTaxRules() {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/configure/tax-deductions/get/${encodeURIComponent(selectedFinancialYear)}`);
            // Handle nested response structure: response.data.data
            if (response.data && response.data.data) {
                const data = Array.isArray(response.data.data) ? response.data.data : [response.data.data];
                setIncomeTaxRules(data);
            } else {
                setIncomeTaxRules([]);
            }
        } catch (e) {
            if (e.response && e.response.status === 401) {
                setNotify({
                    isOpen: true,
                    message: 'Session Expired!',
                    type: 'error'
                });
                setTimeout(() => {
                    navigate('/');
                }, 1000);
            } else if (e.response && e.response.status === 404) {
                // API endpoint might not exist yet, set empty array
                setIncomeTaxRules([]);
            } else {
                // Other errors, set empty array
                setIncomeTaxRules([]);
            }
        }
        setLoading(false);
    }

    // Expose refresh function via ref
    useImperativeHandle(ref, () => ({
        refreshTable: getAllIncomeTaxRules
    }));

    useEffect(() => {
        getAllIncomeTaxRules();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Refresh when refreshKey changes
    useEffect(() => {
        if (props.refreshKey && props.refreshKey > 0) {
            getAllIncomeTaxRules();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.refreshKey]);

    const handleDelete = async (delete_id) => {
        setConfirmDialog({
            ...confirmDialog,
            isOpen: false
        });
        try {
            const response = await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/configure/tax-deductions/delete/` + delete_id);
            if (response.status === 200) {
                setNotify({
                    isOpen: true,
                    message: 'Successfully Deleted!',
                    type: 'success'
                });
                getAllIncomeTaxRules();
            }
        } catch (e) {
            if (e.response && e.response.status === 422) {
                setNotify({
                    isOpen: true,
                    message: e.response.data.error_message || 'Failed to delete',
                    type: 'error'
                });
            }
            if (e.response && e.response.status === 401) {
                setNotify({
                    isOpen: true,
                    message: 'Session Expired!',
                    type: 'error'
                });
                setTimeout(() => {
                    navigate('/');
                }, 1000);
            }
        }
    };

    return (
        <>
            {loading ? <Progress /> :
                <div style={{
                    width: '100%',
                    padding: '10px 0',
                    boxSizing: 'border-box'
                }}>
                    <div style={{
                        width: '100%',
                        backgroundColor: '#fff',
                        borderRadius: '8px',
                        overflow: 'hidden'
                    }}>
                        <table style={{
                            width: '100%',
                            borderCollapse: 'collapse',
                            marginLeft: "20px"
                        }} className="table">
                            <thead className="table-dark">
                                <tr>
                                    <th scope="col" style={{ padding: '12px', textAlign: 'right' }}>Standard Deduction (₹)</th>
                                    <th scope="col" style={{ padding: '12px', textAlign: 'center' }}>CESS Rate (%)</th>
                                    <th scope="col" style={{ padding: '12px', textAlign: 'right' }}>Rebate (₹)</th>
                                    <th scope="col" style={{ padding: '12px', textAlign: 'center' }}>Financial Year</th>
                                    <th scope="col" style={{ padding: '12px', textAlign: 'center', width: '200px' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {incomeTaxRules.length > 0 ? (
                                    incomeTaxRules.map((rule, i) => {
                                        const standardDeduction = rule.standard_deduction !== null && rule.standard_deduction !== undefined 
                                            ? rule.standard_deduction 
                                            : (rule.standardDeduction || rule.StandardDeduction || 0);
                                        const cessRate = rule.cess_rate !== null && rule.cess_rate !== undefined 
                                            ? rule.cess_rate 
                                            : (rule.cessRate || rule.CESSRate || rule.cess || 0);
                                        const rebate = rule.rebate !== null && rule.rebate !== undefined 
                                            ? rule.rebate 
                                            : (rule.Rebate || 0);
                                        const financialYear = rule.financial_year || rule.effectiveDate || rule.effective_date || rule.EffectiveDate || '';
                                        const ruleId = rule.id || rule.income_tax_rule_id || i;

                                        return (
                                            <tr key={ruleId} style={{ borderBottom: '1px solid #dee2e6' }}>
                                                <td style={{ padding: '12px', textAlign: 'right' }}>{Number(standardDeduction).toLocaleString('en-IN')}</td>
                                                <td style={{ padding: '12px', textAlign: 'center' }}>{cessRate}%</td>
                                                <td style={{ padding: '12px', textAlign: 'right' }}>{Number(rebate).toLocaleString('en-IN')}</td>
                                                <td style={{ padding: '12px', textAlign: 'center' }}>{financialYear}</td>
                                                <td style={{ padding: '12px', textAlign: 'center' }}>
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-primary btn-sm"
                                                        onClick={() => {
                                                            setFetchId(ruleId);
                                                            setOpenPopup(true);
                                                        }}
                                                        style={{ marginRight: '8px' }}
                                                    >
                                                        <Edit /> Edit
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-danger btn-sm"
                                                        onClick={() =>
                                                            setConfirmDialog({
                                                                isOpen: true,
                                                                title: 'Are you sure to delete this record?',
                                                                subTitle: "You can't undo this operation",
                                                                onConfirm: () => {
                                                                    handleDelete(ruleId);
                                                                },
                                                            })
                                                        }
                                                    >
                                                        <Delete /> Delete
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan="5" style={{ textAlign: "center", padding: "40px", color: '#6c757d' }}>
                                            No Income Tax Rules found. Click "Add Income Tax Rule" to create one.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                    <Popup
                        title={fetch_id ? "Edit Income Tax Rule" : "Add Income Tax Rule"}
                        openPopup={openPopup}
                        setOpenPopup={(open) => {
                            setOpenPopup(open);
                            if (!open) {
                                setFetchId(0); // Reset fetch_id when popup closes
                            }
                        }}
                    >
                        <AddIncomeTaxRulesForm
                            fetch_id={fetch_id}
                            onRefresh={getAllIncomeTaxRules}
                            onCloseModal={() => {
                                setOpenPopup(false);
                                setFetchId(0);
                            }}
                            setOpenPopup={(open) => {
                                setOpenPopup(open);
                                if (!open) {
                                    setFetchId(0); // Reset fetch_id when popup closes
                                }
                            }}
                        />
                    </Popup>
                </div>}
            <Notification
                notify={notify}
                setNotify={setNotify} />
            <ConfirmDialog
                confirmDialog={confirmDialog}
                setConfirmDialog={setConfirmDialog} />
        </> 
    );
});

export default IncomeTaxRulesTable;

